"use client";

import { Trans } from "@lingui/react/macro";
import { InfoIcon } from "lucide-react";
import * as React from "react";
import * as RechartsPrimitive from "recharts";
import { cn } from "@/lib/utils";

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: "", dark: ".dark" } as const;

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    confidenceInterval?: string;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  );
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }

  return context;
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig;
    overlay?: React.ReactNode;
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >["children"];
  }
>(({ id, className, children, config, overlay, ...props }, ref) => {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={cn(
          // workspace 内のメモ tooltip（z=5）より下で、操作中のチャートを前面へ出します。
          "relative isolate hover:z-2 focus-within:z-1 flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-axis-tick-value]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-hidden [&_.recharts-surface]:outline-hidden",
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer
          debounce={200}
          minHeight={0}
          minWidth={0}
          initialDimension={{ width: 1, height: 1 }}
          {...(process.env.NODE_ENV === "test"
            ? { width: 1, height: 1 }
            : undefined)}
        >
          {children}
        </RechartsPrimitive.ResponsiveContainer>
        {overlay}
      </div>
    </ChartContext>
  );
});
ChartContainer.displayName = "Chart";

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_, config]) => config.theme || config.color,
  );

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
      itemConfig.color;
    return color ? `  --color-${key}: ${color};` : null;
  })
  .join("\n")}
}
`,
          )
          .join("\n"),
      }}
    />
  );
};

/** Recharts の配置・表示を使い、必要な場合だけ画面端とメモ操作を補います。 */
const ChartTooltip = ({
  escapeToViewport = false,
  content,
  wrapperStyle,
  ...props
}: React.ComponentProps<typeof RechartsPrimitive.Tooltip> & {
  escapeToViewport?: boolean;
}) => (
  <RechartsPrimitive.Tooltip
    {...props}
    isAnimationActive={escapeToViewport ? false : props.isAnimationActive}
    wrapperStyle={{
      zIndex: 50,
      ...wrapperStyle,
      ...(escapeToViewport && { pointerEvents: "none", width: "max-content" }),
    }}
    content={
      escapeToViewport ? (
        <ChartTooltipFrame tooltipContent={content} />
      ) : (
        content
      )
    }
  />
);
ChartTooltip.displayName = "Tooltip";

type TooltipContentProps = RechartsPrimitive.TooltipContentProps<
  RechartsPrimitive.TooltipValueType,
  string | number
>;

/** SVG のメモマーカーを含め、表示中の有効な要素をブラウザの Tab 順に返します。 */
function getTabStops(container: Element) {
  return Array.from(
    container.querySelectorAll<HTMLElement | SVGElement>(
      "a[href], button, input, select, textarea, [tabindex]",
    ),
  )
    .filter(
      (element) =>
        element.tabIndex >= 0 &&
        !element.matches(":disabled") &&
        !element.closest("[inert]") &&
        element.getClientRects().length > 0 &&
        getComputedStyle(element).visibility === "visible",
    )
    .sort(
      (a, b) =>
        (a.tabIndex || Number.MAX_SAFE_INTEGER) -
        (b.tabIndex || Number.MAX_SAFE_INTEGER),
    );
}

/** 標準 tooltip をチャート内に保ち、画面外へ出る分だけ位置を補正します。 */
function ChartTooltipFrame({
  tooltipContent,
  active = false,
  payload = [],
  accessibilityLayer = false,
  activeIndex,
  coordinate,
  ...rest
}: Partial<TooltipContentProps> & {
  tooltipContent: React.ComponentProps<
    typeof RechartsPrimitive.Tooltip
  >["content"];
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const plotArea = RechartsPrimitive.usePlotArea();
  const [interactive, setInteractive] = React.useState(false);
  const contentProps: TooltipContentProps = {
    ...rest,
    active,
    payload,
    accessibilityLayer,
    activeIndex,
    coordinate,
  };

  const updatePosition = React.useEffectEvent(() => {
    const frame = ref.current;
    const wrapper = frame?.parentElement;
    if (!frame || !wrapper || !active) return;
    // WorkspaceView の main はスクロール領域なので、画面とその内側に収めます。
    const main = frame.closest("main")?.getBoundingClientRect();
    const left = Math.max(0, main?.left ?? 0) + 8;
    const top = Math.max(0, main?.top ?? 0) + 8;
    const right =
      Math.min(window.innerWidth, main?.right ?? window.innerWidth) - 8;
    const bottom =
      Math.min(window.innerHeight, main?.bottom ?? window.innerHeight) - 8;
    frame.style.maxWidth = `${Math.max(0, right - left)}px`;
    frame.style.maxHeight = `${Math.max(0, bottom - top)}px`;
    // 内側だけを動かすと元の wrapper が横スクロールを作るため、外側の位置を補正します。
    wrapper.style.left = "0px";
    wrapper.style.top = "0px";
    const bounds = wrapper.getBoundingClientRect();
    let x = Math.max(left - bounds.left, Math.min(0, right - bounds.right));
    const chart = frame.closest(".recharts-wrapper")?.getBoundingClientRect();
    const selectedX =
      chart && coordinate?.x != null ? chart.left + coordinate.x : undefined;
    // 画面端へ押し戻して選択日を覆う場合は、空いている側へ逃がします。
    if (
      rest.allowEscapeViewBox?.x &&
      selectedX != null &&
      bounds.left + x < selectedX &&
      bounds.right + x > selectedX
    ) {
      const gap =
        typeof rest.offset === "number" ? rest.offset : (rest.offset?.x ?? 10);
      if (selectedX + gap + bounds.width <= right) {
        x = selectedX + gap - bounds.left;
      } else if (selectedX - gap - bounds.width >= left) {
        x = selectedX - gap - bounds.width - bounds.left;
      }
    }
    const y = Math.max(top - bounds.top, Math.min(0, bottom - bounds.bottom));
    wrapper.style.left = `${x}px`;
    wrapper.style.top = `${y}px`;
  });

  React.useLayoutEffect(() => {
    updatePosition();
  });

  React.useLayoutEffect(() => {
    const frame = ref.current;
    if (!frame || !active) return;
    const observer = new ResizeObserver(updatePosition);
    observer.observe(frame);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [active]);

  React.useEffect(() => {
    const frame = ref.current;
    const chart = frame?.closest<HTMLElement>(".recharts-wrapper");
    if (!frame || !chart) return;
    const chartStops = () =>
      getTabStops(chart).filter((element) => !frame.contains(element));
    // Recharts は tooltip を SVG より前に挿入するため、チャート→メモ→次の操作の順に繋ぎます。
    const tab = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || event.defaultPrevented) return;
      const stops = getTabStops(frame);
      const chartTabStops = chartStops();
      const last = chartTabStops.at(-1);
      if (!last || !stops.length) return;
      if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        stops[0]?.focus();
      } else if (event.shiftKey && event.target === chartTabStops[0]) {
        // SVG より前の tooltip を飛び越え、標準の逆順移動でチャート外へ戻します。
        stops[0]?.focus({ preventScroll: true });
      } else if (event.target === stops.at(event.shiftKey ? 0 : -1)) {
        if (event.shiftKey) event.preventDefault();
        last.focus({ preventScroll: true });
      }
    };
    const enter = () => setInteractive(true);
    const leave = (event: MouseEvent) => {
      if (
        event.relatedTarget instanceof Node &&
        chart.contains(event.relatedTarget)
      )
        return;
      setInteractive(chart.contains(document.activeElement));
    };
    // SVG とメモの間のフォーカス移動では、選択中の日付を失わせません。
    const blur = (event: FocusEvent) => {
      if (
        event.relatedTarget instanceof Node &&
        chart.contains(event.relatedTarget)
      )
        event.stopPropagation();
      else setInteractive(false);
    };
    chart.addEventListener("touchstart", enter, { passive: true });
    chart.addEventListener("mouseenter", enter);
    // tooltip が退出中に外れると mouseleave が届かないため、mouseout を先に捕捉します。
    chart.addEventListener("mouseout", leave, true);
    chart.addEventListener("mouseleave", leave);
    chart.addEventListener("focusin", enter, true);
    chart.addEventListener("keydown", tab);
    chart.addEventListener("focusout", blur, true);
    return () => {
      chart.removeEventListener("touchstart", enter);
      chart.removeEventListener("mouseenter", enter);
      chart.removeEventListener("mouseout", leave, true);
      chart.removeEventListener("mouseleave", leave);
      chart.removeEventListener("focusin", enter, true);
      chart.removeEventListener("keydown", tab);
      chart.removeEventListener("focusout", blur, true);
    };
  }, []);

  return (
    <div
      ref={ref}
      inert={!interactive}
      className="pointer-events-auto w-max max-w-[calc(100vw-16px)] max-h-[calc(100vh-16px)] overflow-auto"
      onMouseMove={(event) => {
        // メモやリンクを選ぶ間だけ、選択中の日付を固定します。
        if (
          event.target instanceof Element &&
          event.target.closest("button, a[href]")
        ) {
          event.stopPropagation();
          return;
        }
        const chart = event.currentTarget
          .closest(".recharts-wrapper")
          ?.getBoundingClientRect();
        // 描画領域外へはみ出した部分では、日付を消さずにスクロールできるようにします。
        if (
          !chart ||
          !plotArea ||
          event.clientX < chart.left + plotArea.x ||
          event.clientX > chart.left + plotArea.x + plotArea.width ||
          event.clientY < chart.top + plotArea.y ||
          event.clientY > chart.top + plotArea.y + plotArea.height
        )
          event.stopPropagation();
      }}
      onTouchMove={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key.startsWith("Arrow") || event.key === "Enter")
          event.stopPropagation();
      }}
    >
      {React.isValidElement<TooltipContentProps>(tooltipContent) ? (
        React.cloneElement(tooltipContent, contentProps)
      ) : typeof tooltipContent === "function" ? (
        React.createElement(tooltipContent, contentProps)
      ) : (
        <RechartsPrimitive.DefaultTooltipContent {...contentProps} />
      )}
    </div>
  );
}

export type ChartTooltipFooterContext = {
  label: unknown;
  payload:
    | RechartsPrimitive.TooltipContentProps<
        number | string,
        string | number
      >["payload"]
    | undefined;
  payloadData?: Record<string, unknown>;
};

type ChartTooltipFooter =
  | React.ReactNode
  | ((context: ChartTooltipFooterContext) => React.ReactNode);

/**
 * Recharts が tooltip content に渡す現在の label/payload から footer を決定します。
 * 外側の mouse state に依存させず、tooltip が実際に表示している bucket と追加情報を同期させるための境界です。
 */
function renderChartTooltipFooter(
  footer: ChartTooltipFooter | undefined,
  context: ChartTooltipFooterContext,
) {
  if (typeof footer === "function") {
    return footer(context);
  }
  return footer ?? null;
}

const ChartTooltipContent = React.memo(
  React.forwardRef<
    HTMLDivElement,
    Partial<
      RechartsPrimitive.TooltipContentProps<
        RechartsPrimitive.TooltipValueType,
        string | number
      >
    > &
      React.ComponentProps<"div"> & {
        hideLabel?: boolean;
        hideIndicator?: boolean;
        indicator?: "line" | "dot" | "dashed";
        nameKey?: string;
        labelKey?: string;
        formatToPercent?: boolean;
        formatToPercentFields?: string[];
        resolveScaledValue?: boolean;
        sort?: "asc" | "desc";
        errorMessage?: string;
        allowedKeys?: string[];
        orderKeys?: string[];
        isGrouped?: boolean; // グループ化されているかどうか
        hideZeroLikeValues?: boolean;
        footer?: ChartTooltipFooter;
      }
  >(
    (
      {
        active,
        payload,
        className,
        indicator = "dot",
        hideLabel = false,
        hideIndicator = false,
        label,
        labelFormatter,
        labelClassName,
        formatter,
        color,
        nameKey,
        labelKey,
        formatToPercent = false,
        formatToPercentFields,
        resolveScaledValue = false,
        sort,
        errorMessage,
        allowedKeys = [],
        orderKeys,
        isGrouped = false,
        hideZeroLikeValues = true,
        footer,
      },
      ref,
    ) => {
      const { config } = useChart();

      const tooltipLabel = React.useMemo(() => {
        if (hideLabel || !payload?.length) {
          return null;
        }

        const [item] = payload;
        const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);

        const normalizedLabel =
          typeof label === "string" || typeof label === "number"
            ? label
            : undefined;
        const normalizedLabelKey =
          typeof normalizedLabel === "number"
            ? String(normalizedLabel)
            : normalizedLabel;

        const value =
          !labelKey && normalizedLabelKey
            ? config[normalizedLabelKey as keyof typeof config]?.label ||
              normalizedLabel
            : itemConfig?.label;

        if (labelFormatter) {
          return (
            <div className={cn("font-medium", labelClassName)}>
              {labelFormatter(value, payload)}
            </div>
          );
        }

        if (!value) {
          return null;
        }

        return <div className={cn("font-medium", labelClassName)}>{value}</div>;
      }, [
        label,
        labelFormatter,
        payload,
        hideLabel,
        labelClassName,
        config,
        labelKey,
      ]);

      const payloadItems = payload ?? [];
      type PayloadItem = (typeof payloadItems)[number];
      const tooltipFooter = renderChartTooltipFooter(footer, {
        label,
        payload,
        payloadData: payloadItems[0]?.payload as
          | Record<string, unknown>
          | undefined,
      });

      // ツールチップに表示してよいキーかどうかを判定するための補助関数群
      const confidenceIntervals: string[] = Object.values(config)
        .filter((c) => c.confidenceInterval)
        .map((c) => c.confidenceInterval as string);

      const isPercentField = React.useCallback(
        (key: string) =>
          Boolean(formatToPercent || formatToPercentFields?.includes(key)),
        [formatToPercent, formatToPercentFields],
      );

      const resolveRawValue = (item: PayloadItem, key: string): unknown => {
        // スケール前の素の値を優先的に取り出す（グループ化時はスケールが無い前提）
        if (!resolveScaledValue) return item.value;

        const rawPayload = item.payload ?? {};

        // _scaled 系は元のキーに戻して参照
        if (key.endsWith("_scaled")) {
          const baseKey = key.replace(/_scaled$/, "");
          if (baseKey in rawPayload) return rawPayload[baseKey];
          if (!isGrouped) {
            const baseOriginalKey = `${baseKey}_original`;
            if (baseOriginalKey in rawPayload) {
              return rawPayload[baseOriginalKey];
            }
          }
        }

        // 非グループ化で _original が存在する場合はそれを優先
        if (!isGrouped) {
          const originalKey = `${key}_original`;
          if (originalKey in rawPayload) {
            return rawPayload[originalKey];
          }
        }

        return item.value;
      };

      const shouldDisplay = (item: PayloadItem) => {
        const itemKey = `${nameKey || item.name || item.dataKey || "value"}`;

        // CI 系列はツールチップ対象外
        if (confidenceIntervals.some((c) => itemKey === c)) return false;

        // allowedKeys が指定されていればそれ以外を除外
        if (allowedKeys.length > 0 && !allowedKeys.includes(itemKey)) {
          return false;
        }

        const rawValue = resolveRawValue(item, itemKey);
        const percentField = isPercentField(itemKey);

        // 0 はノイズになりやすいため、パーセント/非パーセントを問わず除外する。
        // パーセント型は小数形式（0-1）で保持されるため、0.00% に丸め込まれる極小値も除外対象とする。
        const isZeroLike =
          typeof rawValue === "number" &&
          Number.isFinite(rawValue) &&
          (percentField ? Math.abs(rawValue) < 0.00005 : rawValue === 0);
        if (hideZeroLikeValues && isZeroLike) {
          return false;
        }

        return true;
      };

      const orderIndexMap = React.useMemo(() => {
        if (!orderKeys || orderKeys.length === 0) return undefined;
        return new Map(orderKeys.map((key, index) => [key, index]));
      }, [orderKeys]);

      const orderedPayload = React.useMemo(() => {
        const items = [...payloadItems];
        if (orderIndexMap) {
          return items.sort((a, b) => {
            const aKey = `${nameKey || a.name || a.dataKey || "value"}`;
            const bKey = `${nameKey || b.name || b.dataKey || "value"}`;
            const aIndex = orderIndexMap.get(aKey) ?? Number.POSITIVE_INFINITY;
            const bIndex = orderIndexMap.get(bKey) ?? Number.POSITIVE_INFINITY;
            return aIndex - bIndex;
          });
        }

        return items.sort((a, b) => {
          if (
            sort === "asc" &&
            typeof a.value === "number" &&
            typeof b.value === "number"
          ) {
            return a.value - b.value;
          }
          if (
            sort === "desc" &&
            typeof a.value === "number" &&
            typeof b.value === "number"
          ) {
            return b.value - a.value;
          }
          return 0;
        });
      }, [payloadItems, nameKey, orderIndexMap, sort]);

      const filteredPayload = orderedPayload.filter(shouldDisplay);

      // guard は全フック呼び出しの後に置き、フック順序を保つ
      if (!active || !payload || !payload.length) {
        return null;
      }

      const nestLabel = payload.length === 1 && indicator !== "dot";

      const renderItems = (() => {
        const items = filteredPayload.map((item) => {
          const key = `${nameKey || item.name || item.dataKey || "value"}`;
          const rawValue = resolveRawValue(item, key);
          const percentField = isPercentField(key);
          const isZeroPercent =
            percentField && typeof rawValue === "number" && rawValue === 0;

          return {
            item,
            key,
            rawValue,
            isPercentField: percentField,
            isZeroPercent,
          };
        });

        // ゼロ％行は末尾へ寄せ、非ゼロを優先表示
        return items.sort(
          (a, b) => Number(a.isZeroPercent) - Number(b.isZeroPercent),
        );
      })();

      return (
        <div
          ref={ref}
          className={cn(
            "grid items-start gap-1.5 rounded-lg border border-border/50 bg-background/95 px-2.5 py-1.5 text-xs shadow-xl",
            className,
          )}
        >
          {!nestLabel ? tooltipLabel : null}
          <div className="grid gap-1.5">
            {renderItems.map(
              (
                { item, key, rawValue, isPercentField, isZeroPercent },
                index,
              ) => {
                const itemConfig = getPayloadConfigFromPayload(
                  config,
                  item,
                  key,
                );
                const indicatorColor = color || item.color || item.payload.fill;
                let value: React.ReactNode = rawValue as React.ReactNode;

                if (isPercentField && typeof value === "number") {
                  // パーセンテージフィールドは常に小数形式で格納、100倍して表示
                  value = `${(value * 100).toFixed(2)}%`;
                }
                if (typeof value === "number") {
                  value = Number(value.toFixed(2)).toLocaleString();
                }

                const confidenceIntervalValue: [number, number] | undefined =
                  itemConfig?.confidenceInterval
                    ? item.payload[itemConfig.confidenceInterval]
                    : undefined;
                const confidenceInterval = confidenceIntervalValue ? (
                  <div className="my-1 text-muted-foreground py-0.5 text-end">
                    <Trans>CI:</Trans>{" "}
                    <span className="font-mono">
                      {(confidenceIntervalValue[0] * 100).toFixed(2)}% -{" "}
                      {(confidenceIntervalValue[1] * 100).toFixed(2)}%
                    </span>
                  </div>
                ) : undefined;

                return (
                  <div key={String(item.dataKey ?? item.name ?? index)}>
                    <div className="flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground">
                      {formatter && item?.value !== undefined && item.name ? (
                        formatter(
                          item.value,
                          item.name,
                          item,
                          index,
                          item.payload,
                        )
                      ) : (
                        <>
                          {itemConfig?.icon ? (
                            <itemConfig.icon />
                          ) : (
                            !hideIndicator && (
                              <div
                                className={cn(
                                  "shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)",
                                  {
                                    "h-2.5 w-2.5": indicator === "dot",
                                    "w-1": indicator === "line",
                                    "w-0 border-[1.5px] border-dashed bg-transparent":
                                      indicator === "dashed",
                                    "my-0.5":
                                      nestLabel && indicator === "dashed",
                                  },
                                )}
                                style={
                                  {
                                    "--color-bg": indicatorColor,
                                    "--color-border": indicatorColor,
                                  } as React.CSSProperties
                                }
                              />
                            )
                          )}
                          <div
                            className={cn(
                              "flex flex-1 justify-between leading-none",
                              nestLabel ? "items-end" : "items-center",
                            )}
                          >
                            <div className="grid gap-1.5">
                              {nestLabel ? tooltipLabel : null}
                              <span>
                                {itemConfig?.label || item.name || ""}
                              </span>
                            </div>
                            {value !== null &&
                              value !== undefined &&
                              value !== "" && (
                                <span
                                  className={cn(
                                    "font-mono font-medium tabular-nums text-foreground ml-4",
                                    isZeroPercent && "text-muted-foreground",
                                  )}
                                >
                                  {value}
                                </span>
                              )}
                          </div>

                          {item.value === null && (
                            <div className="text-muted-foreground">N/A</div>
                          )}
                        </>
                      )}
                    </div>
                    {confidenceInterval}
                  </div>
                );
              },
            )}

            {/* 値がnullの場合は、エラーメッセージを表示 */}
            {payload.every((item) => item.value === null) && (
              <div className="text-destructive flex items-start gap-1 max-w-48">
                <InfoIcon className="size-3 mt-[2px] shrink-0" />
                {errorMessage}
              </div>
            )}
          </div>
          {tooltipFooter ? (
            <div className="mt-1 border-t border-border/60 pt-2">
              {tooltipFooter}
            </div>
          ) : null}
        </div>
      );
    },
  ),
);
ChartTooltipContent.displayName = "ChartTooltipContent";

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> &
    Pick<
      RechartsPrimitive.DefaultLegendContentProps,
      "payload" | "verticalAlign"
    > & {
      hideIcon?: boolean;
      nameKey?: string;
    }
>(
  (
    { className, hideIcon = false, payload, verticalAlign = "bottom", nameKey },
    ref,
  ) => {
    const { config } = useChart();

    if (!payload?.length) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-center gap-4",
          verticalAlign === "top" ? "pb-3" : "pt-3",
          className,
        )}
      >
        {payload.map((item) => {
          const key = `${nameKey || item.dataKey || "value"}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);

          return (
            <div
              key={item.value}
              className={cn(
                "flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground",
              )}
            >
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{
                    backgroundColor: item.color,
                  }}
                />
              )}
              {itemConfig?.label}
            </div>
          );
        })}
      </div>
    );
  },
);
ChartLegendContent.displayName = "ChartLegend";

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string,
) {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  const payloadPayload =
    "payload" in payload &&
    typeof payload.payload === "object" &&
    payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === "string"
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string;
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config];
}

/**
 * 比較モード用のチャートツールチップコンポーネント
 * 基準日と比較日のデータを分離して表示し、差分も表示します
 */
const ComparisonChartTooltipContent = React.memo(
  ({
    active,
    payload,
    className,
    comparisonKeyPrefix = "comparing_",
    diffType = "absolute",
    formatToPercent = false,
    formatToPercentFields = [],
    invertDiffColorFields = [],
    resolveScaledValue = false,
    formatBaseDate,
    formatComparisonDate,
    formatValue,
    formatDiff,
    comparisonDateKey = "comparingDate",
    allowedKeys = [],
    isGrouped = false,
    orderKeys,
    sort,
    footer,
  }: Partial<
    RechartsPrimitive.TooltipContentProps<
      RechartsPrimitive.TooltipValueType,
      string | number
    >
  > &
    React.ComponentProps<"div"> & {
      /**
       * 比較データのキープレフィックス
       * 例:"comparing_" の場合、"comparing_sumOfClicks" が比較データのキーとなる
       */
      comparisonKeyPrefix?: string;
      /**
       * 差分の計算方法
       *"absolute": 絶対値 (基準値 - 比較値)
       *"percentage": パーセンテージ ((基準値 - 比較値) / 比較値 * 100)
       */
      diffType?: "absolute" | "percentage";
      /**
       * パーセント表示するかどうか
       */
      formatToPercent?: boolean;
      /**
       * パーセント表示するフィールド名の配列(formatToPercentがtrueの場合は一括でパーセント表示される)
       */
      formatToPercentFields?: string[];
      /**
       * 差分の色を反転するフィールド名の配列
       */
      invertDiffColorFields?: string[];
      /**
       * スケーリングされた値を元の値に戻すかどうか
       */
      resolveScaledValue?: boolean;
      /**
       * 基準日付のフォーマット関数
       */
      formatBaseDate?: (date: string) => string;
      /**
       * 比較日付のフォーマット関数
       */
      formatComparisonDate?: (date: string) => string;
      /**
       * 値のフォーマット関数
       */
      formatValue?: (value: unknown, key: string) => string;
      /**
       * 差分値のフォーマット関数
       */
      formatDiff?: (diff: number, key: string) => string;
      /**
       * 比較日付のキー名
       */
      comparisonDateKey?: string;
      /**
       * 表示を許可するキーの配列（空の場合はすべて表示）
       */
      allowedKeys?: string[];
      /**
       * 表示順のキー配列（指定時はこの順で固定）
       */
      orderKeys?: string[];
      /**
       * グループ化されているかどうか
       */
      isGrouped?: boolean;
      /**
       * 表示順を値でソートするかどうか
       */
      sort?: "asc" | "desc";
      /**
       * 指標一覧の下に表示する追加情報。
       */
      footer?: ChartTooltipFooter;
    }) => {
    const { config } = useChart();
    const payloadData = payload?.[0]?.payload as
      | Record<string, unknown>
      | undefined;
    const activePayloadData = payloadData ?? {};

    // 表示する基準データと比較データを抽出
    const displayItems = React.useMemo(() => {
      if (!payload?.length) return [];

      const items: {
        key: string;
        baseValue: unknown;
        comparisonValue: unknown;
        diff: number | null;
        percentageDiff: number | null;
        color: string;
        label: React.ReactNode;
        confidenceInterval: [number, number] | undefined;
        comparingConfidenceInterval: [number, number] | undefined;
      }[] = [];

      // 基準データのキーを抽出（比較キープレフィックスがついていないもの）
      const baseKeys = payload
        .map((item) => item.dataKey)
        .filter(
          (key): key is string =>
            typeof key === "string" &&
            !key.startsWith(comparisonKeyPrefix) &&
            key !== "date" &&
            key !== comparisonDateKey,
        );

      // 比較データにしか存在しないキーも含める
      const allKeysInPayloadData = Object.keys(activePayloadData);
      const comparisonOnlyKeys = allKeysInPayloadData
        .filter((key) => key.startsWith(comparisonKeyPrefix))
        .map((key) => key.replace(comparisonKeyPrefix, ""))
        .filter((key) => !baseKeys.includes(key))
        // _originalサフィックスのキーは除外（これはスケーリング用の補助データ）
        .filter((key) => !key.endsWith("_original"));

      const allKeys = [...new Set([...baseKeys, ...comparisonOnlyKeys])];

      // confidenceInterval用のキーを事前に取得
      const confidenceIntervals: string[] = Object.values(config)
        .filter((c) => c.confidenceInterval)
        .map((c) => c.confidenceInterval as string);

      for (const baseKey of allKeys) {
        // 基準データのキーがconfidenceIntervalとして指定されている場合はスキップ
        if (confidenceIntervals.includes(baseKey)) continue;

        // allowedKeysが指定されている場合、許可されたキーのみを表示
        if (allowedKeys.length > 0 && !allowedKeys.includes(baseKey)) continue;

        // 基準値を取得
        const baseItem = payload.find((item) => item.dataKey === baseKey);

        // 比較キーを生成
        const comparisonKey = `${comparisonKeyPrefix}${baseKey}`;

        // 値の取得（スケーリングされた値の場合は元の値に戻す）
        let baseValue: unknown = baseItem?.value;
        let comparisonValue = activePayloadData[comparisonKey];

        // グループ化されている場合は、そもそもスケーリングされていないので元の値解決は不要
        if (resolveScaledValue && !isGrouped) {
          // 従来の_scaledサフィックス処理
          if (typeof baseKey === "string" && baseKey.endsWith("_scaled")) {
            const originalKey = baseKey.replace(/_scaled$/, "");
            if (baseItem && originalKey in baseItem.payload) {
              baseValue = baseItem.payload[originalKey];
            } else if (!baseItem && originalKey in activePayloadData) {
              baseValue = activePayloadData[originalKey];
            }

            const comparisonOriginalKeySuffix = `${comparisonKeyPrefix}${originalKey}`;
            if (comparisonOriginalKeySuffix in activePayloadData) {
              comparisonValue = activePayloadData[comparisonOriginalKeySuffix];
            }
          }

          // 非グループ化でのオリジナル値取得
          const baseOriginalKey = `${baseKey}_original`;
          const comparisonOriginalKey = `comparing_${baseKey}_original`;

          if (baseOriginalKey in activePayloadData) {
            baseValue = activePayloadData[baseOriginalKey];
          }

          if (comparisonOriginalKey in activePayloadData) {
            comparisonValue = activePayloadData[comparisonOriginalKey];
          }
        }

        // 差分の計算
        let diff: number | null = null;
        let percentageDiff: number | null = null;
        if (
          comparisonValue !== undefined &&
          comparisonValue !== null &&
          typeof baseValue === "number" &&
          typeof comparisonValue === "number"
        ) {
          if (diffType === "absolute") {
            diff = baseValue - comparisonValue;
            // 絶対値差分の場合もパーセンテージを計算
            if (comparisonValue !== 0) {
              percentageDiff =
                ((baseValue - comparisonValue) / comparisonValue) * 100;
            }
          } else if (diffType === "percentage" && comparisonValue !== 0) {
            diff = ((baseValue - comparisonValue) / comparisonValue) * 100;
          }

          // パーセント型フィールドの場合は常にパーセンテージを計算
          const isPercentField =
            formatToPercentFields.includes(String(baseKey)) || formatToPercent;
          if (
            isPercentField &&
            comparisonValue !== 0 &&
            percentageDiff === null
          ) {
            percentageDiff =
              ((baseValue - comparisonValue) / comparisonValue) * 100;
          }
        }

        // 表示用のアイテムを作成
        const itemConfig = baseItem
          ? getPayloadConfigFromPayload(
              config,
              baseItem,
              typeof baseKey === "string" ? baseKey : String(baseKey),
            )
          : config[baseKey];

        // baseItemがない場合は、比較データから色を取得するか、configから取得
        let color = "";
        if (baseItem) {
          color = baseItem.color || baseItem.stroke || "";
        } else {
          // 比較データのアイテムから色を推測
          const comparisonItem = payload.find(
            (item) => item.dataKey === comparisonKey,
          );
          if (comparisonItem) {
            color = comparisonItem.color || comparisonItem.stroke || "";
          } else if (itemConfig?.color) {
            color = itemConfig.color;
          }
        }

        // 基準/比較の両側が「欠損または 0」と見なせる行はノイズになりやすいため除外する。
        // パーセント型は小数(0-1)で保持されるため、表示が 0.00% に丸め込まれる極小値も 0 とみなす。
        const isPercentFieldForRow =
          formatToPercentFields.includes(String(baseKey)) || formatToPercent;
        const isZeroLikeNumber = (value: unknown): boolean =>
          typeof value === "number" &&
          Number.isFinite(value) &&
          (isPercentFieldForRow ? Math.abs(value) < 0.00005 : value === 0);

        const baseZeroLike =
          baseValue === null ||
          baseValue === undefined ||
          isZeroLikeNumber(baseValue);
        const comparisonZeroLike =
          comparisonValue === null ||
          comparisonValue === undefined ||
          isZeroLikeNumber(comparisonValue);

        if (baseZeroLike && comparisonZeroLike) {
          continue;
        }

        const displayLabel =
          itemConfig?.label ??
          (typeof baseItem?.name === "string" ||
          typeof baseItem?.name === "number"
            ? baseItem.name
            : baseKey);

        items.push({
          key: String(baseKey),
          baseValue,
          comparisonValue,
          diff,
          percentageDiff,
          color,
          label: displayLabel,
          confidenceInterval:
            itemConfig?.confidenceInterval && baseItem
              ? baseItem.payload[itemConfig.confidenceInterval]
              : undefined,
          comparingConfidenceInterval: itemConfig?.confidenceInterval
            ? (activePayloadData[
                `${comparisonKeyPrefix}${itemConfig.confidenceInterval}`
              ] as [number, number] | undefined)
            : undefined,
        });
      }

      return items;
    }, [
      payload,
      comparisonKeyPrefix,
      resolveScaledValue,
      diffType,
      config,
      comparisonDateKey,
      formatToPercentFields,
      formatToPercent,
      allowedKeys,
      isGrouped,
      activePayloadData,
    ]);

    // 基準日付と比較日付を取得
    const baseDate = payloadData?.date;
    const comparisonDate = payloadData?.[comparisonDateKey];
    const tooltipFooter = renderChartTooltipFooter(footer, {
      label: baseDate,
      payload,
      payloadData,
    });

    // 期間比較時に比較値が存在しない（N/A）行は非表示にする
    const visibleItems = React.useMemo(() => {
      if (!comparisonDate) return displayItems;
      return displayItems.filter(
        (item) =>
          item.comparisonValue !== null && item.comparisonValue !== undefined,
      );
    }, [displayItems, comparisonDate]);

    const orderIndexMap = React.useMemo(() => {
      if (!orderKeys || orderKeys.length === 0) return undefined;
      return new Map(orderKeys.map((key, index) => [key, index]));
    }, [orderKeys]);

    const sortedVisibleItems = React.useMemo(() => {
      const arr = [...visibleItems];
      if (orderIndexMap) {
        return arr.sort((a, b) => {
          const aIndex = orderIndexMap.get(a.key) ?? Number.POSITIVE_INFINITY;
          const bIndex = orderIndexMap.get(b.key) ?? Number.POSITIVE_INFINITY;
          return aIndex - bIndex;
        });
      }
      if (!sort) return arr;
      return arr.sort((a, b) => {
        if (
          typeof a.baseValue === "number" &&
          typeof b.baseValue === "number"
        ) {
          return sort === "asc"
            ? a.baseValue - b.baseValue
            : b.baseValue - a.baseValue;
        }
        return 0;
      });
    }, [visibleItems, orderIndexMap, sort]);

    const orderedVisibleItems = React.useMemo(() => {
      const isZeroPercentItem = (item: (typeof visibleItems)[number]) => {
        const isPercentField =
          formatToPercentFields.includes(item.key) || formatToPercent;
        return (
          isPercentField &&
          typeof item.baseValue === "number" &&
          item.baseValue === 0 &&
          typeof item.comparisonValue === "number" &&
          item.comparisonValue === 0
        );
      };

      const nonZero = sortedVisibleItems.filter(
        (item) => !isZeroPercentItem(item),
      );
      const zero = sortedVisibleItems.filter((item) => isZeroPercentItem(item));
      return [...nonZero, ...zero];
    }, [sortedVisibleItems, formatToPercent, formatToPercentFields]);

    if (!active || !payload || !payload.length) {
      return null;
    }

    // 値のフォーマット
    const formatValueFn = (value: unknown, key: string): string => {
      if (value === undefined || value === null) return "N/A";

      // カスタムフォーマッタがある場合はそれを使用
      if (formatValue) {
        return formatValue(value, key);
      }

      // パーセント表示が必要なフィールドの場合
      if (
        typeof value === "number" &&
        (formatToPercentFields.includes(key) || formatToPercent)
      ) {
        // パーセンテージフィールドは常に小数形式で格納、100倍して表示
        return `${(value * 100).toFixed(2)}%`;
      }

      // 数値の場合は小数点以下2桁までにフォーマット
      if (typeof value === "number") {
        return Number(value.toFixed(2)).toLocaleString();
      }

      return String(value);
    };

    // 差分のフォーマット
    const formatDiffFn = (diff: number | null, key: string): string => {
      if (diff === null) return "";

      // カスタムフォーマッタがある場合はそれを使用
      if (formatDiff) {
        return formatDiff(diff, key);
      }

      const prefix = diff === 0 ? "" : diff > 0 ? "+" : "";

      // パーセント表示が必要なフィールドの場合
      if (
        (formatToPercentFields.includes(key) || formatToPercent) &&
        diffType !== "percentage"
      ) {
        // パーセンテージポイント差分は常に小数形式で格納、100倍して表示
        return `${prefix}${(diff * 100).toFixed(2)}pp`;
      }

      // パーセンテージ差分の場合
      if (diffType === "percentage") {
        return `${prefix}${diff.toFixed(2)}%`;
      }

      // 通常の差分
      return `${prefix}${Number(diff.toFixed(2)).toLocaleString()}`;
    };

    // 差分の色
    const diffColor = (diff: number | null, key: string): string => {
      if (diff === null) return "";
      if (diff === 0) return "text-muted-foreground";
      if (invertDiffColorFields.includes(key)) {
        return diff > 0
          ? "text-red-600 dark:text-red-400"
          : "text-green-600 dark:text-green-400";
      }
      return diff > 0
        ? "text-green-600 dark:text-green-400"
        : "text-red-600 dark:text-red-400";
    };

    // 日付のフォーマット
    const formatBaseDateFn = (date: unknown): string => {
      const dateStr = typeof date === "string" ? date : String(date);
      return formatBaseDate ? formatBaseDate(dateStr) : dateStr;
    };

    const formatComparisonDateFn = (date: unknown): string => {
      const dateStr = typeof date === "string" ? date : String(date);
      return formatComparisonDate ? formatComparisonDate(dateStr) : dateStr;
    };

    const formatConfidenceIntervalFn = (
      confidenceInterval: [number, number] | undefined,
    ): React.ReactNode => {
      return confidenceInterval ? (
        <span className="text-muted-foreground text-end">
          <Trans>CI:</Trans>{" "}
          <span className="font-mono">
            {(confidenceInterval[0] * 100).toFixed(2)}% -{" "}
            {(confidenceInterval[1] * 100).toFixed(2)}%
          </span>
        </span>
      ) : null;
    };

    return (
      <div
        className={cn(
          "grid w-max max-w-[min(100%,calc(100vw-16px))] items-start gap-3 rounded-lg border border-border/50 bg-background/95 px-3 py-2 text-xs shadow-xl",
          className,
        )}
      >
        <div className="flex gap-3 max-[640px]:flex-col">
          {/* 基準列 */}
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            {baseDate !== undefined && baseDate !== null ? (
              <div className="font-medium break-words">
                {formatBaseDateFn(baseDate)}
              </div>
            ) : null}

            <div className="grid gap-1.5">
              {orderedVisibleItems.map((item) => (
                <div key={`base-${item.key}`}>
                  <div className="grid min-h-4 grid-cols-[auto_minmax(80px,1fr)_auto_auto] items-start gap-x-2 max-[640px]:grid-cols-[auto_minmax(0,1fr)_auto]">
                    <div className="flex h-4 items-center">
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                        style={{ backgroundColor: item.color }}
                      />
                    </div>
                    <span className="block min-h-4 min-w-0 truncate leading-4 max-[640px]:whitespace-normal max-[640px]:break-words">
                      {item.label}
                    </span>
                    <span
                      className={cn(
                        "flex min-h-4 min-w-[4em] items-center justify-end whitespace-nowrap font-mono text-xs tabular-nums text-right leading-none max-[640px]:col-start-2 max-[640px]:col-end-4 max-[640px]:row-start-2 max-[640px]:mt-1",
                        item.diff !== null
                          ? diffColor(item.diff, item.key)
                          : "text-muted-foreground",
                      )}
                    >
                      {item.diff !== null ? (
                        <>
                          {formatDiffFn(item.diff, item.key)}
                          {item.percentageDiff !== null && (
                            <span className="ml-1 text-[11px]">
                              ({item.percentageDiff > 0 ? "+" : ""}
                              {item.percentageDiff.toFixed(2)}%)
                            </span>
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </span>
                    <span
                      className={cn(
                        "flex min-h-4 min-w-[4em] items-center justify-end whitespace-nowrap font-mono font-semibold tabular-nums text-foreground text-right leading-none max-[640px]:col-start-3 max-[640px]:row-start-1",
                        (item.baseValue === null ||
                          item.baseValue === undefined) &&
                          "text-muted-foreground",
                      )}
                    >
                      <span
                        className={cn(
                          "leading-none",
                          formatToPercentFields.includes(item.key) ||
                            formatToPercent
                            ? item.baseValue === 0 &&
                                item.comparisonValue === 0 &&
                                "text-muted-foreground"
                            : undefined,
                        )}
                      >
                        {formatValueFn(item.baseValue, item.key)}
                      </span>
                    </span>
                  </div>
                  {item.confidenceInterval && (
                    <div className="pl-[calc(0.625rem+0.5rem)] text-muted-foreground text-xs text-right">
                      {formatConfidenceIntervalFn(item.confidenceInterval)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 縦区切り＋比較列 */}
          {comparisonDate !== undefined && comparisonDate !== null ? (
            <div className="flex shrink-0 flex-col gap-1.5 w-fit border-l border-border/50 pl-2 max-[640px]:w-auto max-[640px]:border-l-0 max-[640px]:border-t max-[640px]:pl-0 max-[640px]:pt-2">
              <div className="font-medium break-words text-muted-foreground">
                {formatComparisonDateFn(comparisonDate)}
              </div>

              <div className="grid gap-1.5">
                {orderedVisibleItems.map((item) => (
                  <div key={`comparison-${item.key}`}>
                    <div className="grid min-h-4 grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2 max-[640px]:grid-cols-[auto_minmax(0,1fr)_auto]">
                      <div className="flex h-4 items-center">
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-[2px] border-[1.5px] border-dashed bg-transparent"
                          style={{ borderColor: item.color }}
                        />
                      </div>
                      <span className="hidden min-w-0 break-words leading-4 max-[640px]:block">
                        {item.label}
                      </span>
                      <span className="flex min-h-4 items-center justify-end whitespace-nowrap font-mono font-semibold tabular-nums text-muted-foreground text-right leading-none">
                        {formatValueFn(item.comparisonValue, item.key)}
                      </span>
                    </div>
                    {item.comparingConfidenceInterval && (
                      <div className="pl-[calc(0.625rem+0.5rem)] text-muted-foreground text-xs text-right">
                        {formatConfidenceIntervalFn(
                          item.comparingConfidenceInterval,
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        {tooltipFooter ? (
          <div className="border-t border-border/60 pt-2">{tooltipFooter}</div>
        ) : null}
      </div>
    );
  },
);

ComparisonChartTooltipContent.displayName = "ComparisonChartTooltipContent";

export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
  ComparisonChartTooltipContent,
};
